import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Button from '../../../components/ui/Button';

import ConfirmationModal from '../../../components/ui/ConfirmationModal';
import { appointmentService } from '../../../services/appointmentService';
import { AlertTriangle } from 'lucide-react';
import { useAvailableTimeSlots } from 'hooks';

const today = new Date()?.toISOString()?.split('T')?.[0];

const AppointmentModal = ({ 
  isOpen, 
  onClose, 
  onSave, 
  selectedDate, 
  selectedTime, 
  patients, 
  patientsLoading,
  editingAppointment,
  fromPatientManagement,
  preSelectedPatientId,
  psychologistProfile
}) => {

const {
  availableTimeSlots,
  loadingAvailableTimeSlots,
  errorAvailableTimeSlots,
  getAvailableTimeSlots,
} = useAvailableTimeSlots();  

  const [formData, setFormData] = useState({
    patientId: '',
    date: today,
    time: '',
    duration: '50',
    value: '150.00',
    sendWhatsApp: true,
    reminderTime: '6'
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [showConflictModal, setShowConflictModal] = useState(false);
  const [conflictData, setConflictData] = useState(null);
  const [pendingAppointmentData, setPendingAppointmentData] = useState(null);
  const [loadingTimeSlots, setLoadingTimeSlots] = useState(false);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.width = '100%';
    } else {
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
    }

    return () => {
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
    };
  }, [isOpen]);

  useEffect(() => {
    if (editingAppointment) {
      const selectedPatient = patients?.find(p => p?.id === editingAppointment?.patient_id);
      // Extract time in HH:mm format to match availableTimeSlots format
      const startTimeFormatted = editingAppointment?.start_time?.substring(0, 5); // Convert HH:mm:ss to HH:mm
      setFormData({
        patientId: editingAppointment?.patient_id,
        date: new Date(editingAppointment?.appointment_date)?.toISOString()?.split('T')?.[0],
        time: startTimeFormatted,
        duration: editingAppointment?.duration?.toString(),
        value: selectedPatient?.consultation_price?.toString() || '150.00',
        sendWhatsApp: true,
        reminderTime: '6'
      });
    } else if (selectedDate && selectedTime) {      
      const initialData = {
        date: selectedDate?.toISOString()?.split('T')?.[0],
        time: selectedTime
      };
      
      // If a patient is pre-selected, set it and their consultation price
      if (preSelectedPatientId) {
        const preSelectedPatient = patients?.find(p => p?.id === preSelectedPatientId);
        initialData.patientId = preSelectedPatientId;
        if (preSelectedPatient?.consultation_price) {
          initialData.value = preSelectedPatient?.consultation_price?.toString();
        }
      }
      
      setFormData(prev => ({
        ...prev,
        ...initialData
      }));
    } else if (preSelectedPatientId && !editingAppointment) {
      // When only patient is pre-selected (no date/time)
      const preSelectedPatient = patients?.find(p => p?.id === preSelectedPatientId);
      setFormData(prev => ({
        ...prev,
        patientId: preSelectedPatientId,
        value: preSelectedPatient?.consultation_price?.toString() || prev?.value
      }));
    }
  }, [editingAppointment, selectedDate, selectedTime, patients, preSelectedPatientId]);

  // ESC key handler to close modal
  useEffect(() => {
    const handleEscapeKey = (event) => {
      if (event?.key === 'Escape' && isOpen && !isSubmitting) {
        handleClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscapeKey);
    }

    return () => {
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [isOpen, isSubmitting]);

  // Load available time slots
  useEffect(() => {
    const loadAvailableTimeSlots = async () => {
      if (!formData?.date || !psychologistProfile) {
        return;
      }

      try {
        const dateString = formData?.date;
        const [year, month, day] = dateString.split('-').map(Number);
        const novaData = new Date(year, month - 1, day);

        //const isoString = novaData?.toLocaleDateString('sv');
        //const normalizedDate = isoString ? new Date(`${isoString}T00:00:00`) : null;

        console.log(novaData);
        const removeBlocked = true;
        await getAvailableTimeSlots(novaData, psychologistProfile.id, psychologistProfile.work_lunch_interval, removeBlocked);
      } catch (err) {
        console.error('Error loading available time slots:', err);
        setErrors('Erro ao carregar horários disponíveis');
      }
    };

    loadAvailableTimeSlots();
  
  }, [formData?.date, psychologistProfile, getAvailableTimeSlots, fromPatientManagement]);

  const patientOptions = patients?.map(patient => ({
    value: patient?.id,
    label: patient?.full_name,
    description: patient?.whatsapp_number || patient?.phone
  })) || [];

  const durationOptions = [
    { value: '30', label: '30 minutos' },
    { value: '45', label: '45 minutos' },
    { value: '50', label: '50 minutos' },
    { value: '60', label: '60 minutos' },
    { value: '90', label: '90 minutos' }
  ];

  const reminderOptions = [
    { value: '6', label: '6 horas antes' },
    { value: '8', label: '8 horas antes' },
    { value: '12', label: '12 horas antes' },
    { value: '24', label: '24 horas antes' }
  ];

  const handleInputChange = (e) => {
    const { name, value } = e?.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors?.[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleSelectChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors?.[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }

    // Update consultation price when patient is selected
    if (name === 'patientId') {
      const selectedPatient = patients?.find(p => p?.id === value);
      if (selectedPatient?.consultation_price) {
        setFormData(prev => ({ 
          ...prev, 
          value: selectedPatient?.consultation_price?.toString() 
        }));
      }
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.patientId) {
      newErrors.patientId = 'Selecione um paciente';
    }
    if (!formData?.date) {
      newErrors.date = 'Data é obrigatória';
    }
    if (!formData?.time) {
      newErrors.time = 'Horário é obrigatório';
    }
    if (!formData?.value || parseFloat(formData?.value) <= 0) {
      newErrors.value = 'Valor deve ser maior que zero';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setIsSubmitting(true);
    setError(null);

    try {
      if (!validateForm()) {
        setIsSubmitting(false);
        return;
      }

      const selectedPatient = patients?.find(p => p?.id === formData?.patientId);
      
      // Calculate end time based on start time and duration
      const [hours, minutes] = formData?.time?.split(':')?.map(Number);
      const startDate = new Date();
      startDate?.setHours(hours, minutes, 0, 0);
      const endDate = new Date(startDate?.getTime() + parseInt(formData?.duration) * 60000);
      const endTime = `${String(endDate?.getHours())?.padStart(2, '0')}:${String(endDate?.getMinutes())?.padStart(2, '0')}`;

      const appointmentData = {
        patient_id: formData?.patientId,
        appointment_date: formData?.date,
        start_time: formData?.time,
        end_time: endTime,
        duration: parseInt(formData?.duration),
        status: editingAppointment?.status || 'scheduled',
        appointment_type: 'therapy_session',
        title: `Consulta - ${selectedPatient?.full_name}`,
        is_online: false
      };

      const { hasConflict, conflicts, error: conflictError } = await appointmentService?.checkConflicts(
        psychologistProfile.id,
        appointmentData?.appointment_date,
        appointmentData?.start_time,
        appointmentData?.end_time,
        editingAppointment?.id // Exclude current appointment if editing
      );

      if (conflictError) {
        throw conflictError;
      }

      if (hasConflict) {
        setConflictData(conflicts);
        setPendingAppointmentData(appointmentData);
        setShowConflictModal(true);
        setIsSubmitting(false);
        return;
      }

      await saveAppointment(appointmentData);
      
    } catch (err) {
      setError(err?.message || 'Erro ao salvar a consulta');
      setIsSubmitting(false);
    }
  };

  const saveAppointment = async (appointmentData) => {
    try {
      setIsSubmitting(true);
      onSave(appointmentData);
      setSuccess(editingAppointment ? 'Consulta atualizada com sucesso!' : 'Consulta criada com sucesso!');
      handleClose();
    } catch (err) {
      setError(err?.message || 'Erro ao salvar a consulta');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleConflictConfirm = async () => {
    setShowConflictModal(false);
    await saveAppointment(pendingAppointmentData);
  };

  const handleConflictCancel = () => {
    setShowConflictModal(false);
    setConflictData(null);
    setPendingAppointmentData(null);
  };

  const handleClose = () => {
    setFormData({
      patientId: '',
      date: '',
      time: '',
      duration: '50',
      value: '150.00',
      sendWhatsApp: true,
      reminderTime: '6'
    });
    setErrors({});
    onClose();
  };

  if (!isOpen) return null;

  return (
    <>
      <div 
        className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm overflow-y-auto"
        onClick={handleClose}
      >
        <div 
          className="bg-card rounded-xl shadow-2xl border border-border w-full max-w-2xl max-h-[95vh] flex flex-col overflow-hidden mx-auto my-auto"
          style={{ minHeight: '90vh', maxHeight: '96vh' }}
          onClick={(e) => e?.stopPropagation()}
        >
          <div className="sticky top-0 bg-card border-b border-border px-6 py-4 flex items-center justify-between z-10 shadow-sm">
            <h2 className="text-xl font-semibold text-foreground">
              {editingAppointment ? 'Editar Consulta' : 'Nova Consulta'}
            </h2>
            <button
              onClick={handleClose}
              className="w-8 h-8 rounded-lg hover:bg-muted transition-smooth flex items-center justify-center"
            >
              <Icon name="X" size={20} />
            </button>
          </div>

          <div className="overflow-y-auto flex-1">
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <Select
                label="Paciente"
                options={patientOptions}
                value={formData?.patientId}
                onChange={(value) => handleSelectChange('patientId', value)}
                error={errors?.patientId}
                placeholder={patientsLoading ? "Carregando pacientes..." : "Selecione um paciente"}
                disabled={editingAppointment}
                required
              />

              {patientsLoading && (
                <p className="text-sm text-gray-500">Carregando lista de pacientes...</p>
              )}

              {!patientsLoading && patientOptions?.length === 0 && (
                <p className="text-sm text-amber-600">
                  Nenhum paciente ativo encontrado. Cadastre um paciente primeiro.
                </p>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  label="Data"
                  type="date"
                  name="date"
                  value={formData?.date}
                  onChange={handleInputChange}
                  error={errors?.date}
                  required
                />
                <Select
                  label="Horários Disponíveis"
                  options={availableTimeSlots}
                  value={formData?.time}
                  onChange={(value) => handleSelectChange('time', value)}
                  error={errors?.time}
                  placeholder={
                    loadingTimeSlots 
                      ? "Carregando horários..." 
                      : availableTimeSlots?.length === 0 
                        ? "Nenhum horário disponível" :"Selecione o horário"
                  }
                  disabled={!formData?.date || loadingTimeSlots}
                  required
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-start">
                <Select
                  label="Duração"
                  options={durationOptions}
                  value={formData?.duration}
                  onChange={(value) => handleSelectChange('duration', value)}
                  placeholder="Selecione a duração"
                  className="mt-1"
                />
                <Input
                  label="Valor da Sessão (R$)"
                  type="number"
                  name="value"
                  step="0.01"
                  min="0"
                  value={formData?.value}
                  onChange={handleInputChange}
                  error={errors?.value}
                  disabled
                />
              </div>

              <div className="flex items-center gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleClose}
                  fullWidth
                >
                  Retornar
                </Button>
                <Button
                  type="submit"
                  variant="default"
                  fullWidth
                  iconName="Check"
                  iconPosition="right"
                >
                  {editingAppointment ? 'Salvar Alterações' : 'Criar Consulta'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      </div>

      <ConfirmationModal
        isOpen={showConflictModal}
        onClose={handleConflictCancel}
        onConfirm={handleConflictConfirm}
        title="Conflito de Horário Detectado"
        message="Este horário está ocupado ou haverá sobreposição com outro agendamento. Deseja continuar mesmo assim?"
        confirmText="Continuar"
        cancelText="Retornar"
        confirmButtonClass="bg-amber-600 hover:bg-amber-700 text-white"
        icon={AlertTriangle}
      />
    </>
  );
};

export default AppointmentModal;